import { api } from './api';
import { errorSafe } from 'app/infrastructure/redux';
import { ApplicationState } from 'app/types-store';
import { FavouriteDefinition, ActionDispatching, Value, SelectionDefinition, DefaultSortOrder, 
  FavouriteReplaceDomainNodes } from 'app/types-shared';
import * as actions from './action-types';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { selectWorkingDefinition } from './selectors';
import { LoadFavouriteDefinitionParams, LoadFavouriteDefinitionEmbeddedParams } from './types';
import { FavouriteState } from './types-store';
import * as navigate from 'app/area/shared/navigation';
import { CreateFavouriteDefinitionFailure, LoadFavouriteDefinitionFailure } from 'app/area/favourite/action-types';
import { DisplayModal } from 'app/shell/action-types';
import { MODAL } from 'app/shell/modal-host-types';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { FavouriteReportDownload } from 'app/area/favourite/types';
import { reportTypes, searchTranslation } from './constants';
import { AutoCloseDuration, toastFactory } from 'app/infrastructure/notifications/toast-factory';
import { produce } from 'immer';
import { favouriteDefinitionsAreEqual } from 'app/area/favourite/compare';
import * as favDefTransforms from './favourite-definition-transforms';
import * as navigation from 'app/area/shared/navigation';
import * as companyReportActionCreators from 'app/area/company-report/actions';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { ReportId } from 'app/area/saved-reports/constants';
import { ReceiveCreateSpecialReport, RequestCreateSpecialReport } from 'app/area/company-report/action-types';
import { CaseIncludedField } from 'app/area/case-report/case-listing/types';
import { mapSortOptions } from 'app/area/favourite/mapping';
import { mapQueryParams } from 'app/area/favourite/search-query';

const createDraftAndHandleErrors = async (favouriteId: number, dispatch: any) => {

  const favourite = await api.createDraftDefinition(favouriteId);
  const favouriteData = favourite?.data;
  const favouriteMessages = favourite?.messages;

  !!favouriteMessages?.userMessages?.length
    && favouriteMessages.userMessages.forEach(err =>
      dispatch(new ErrorNotification(err.message))
    );

  return favouriteData;
};

// eslint:disable-next-line: max-func-body-length
export const actionCreators = (instanceKey: string) => ({

  clearResults: (): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new actions.ClearResults(instanceKey));
  },

  createFavouriteDraftDefinition: (favouriteId: number, report: number, editSavedFavourite: boolean): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const favouriteData = await createDraftAndHandleErrors(favouriteId, dispatch);
        navigation.toCaseListing(favouriteData.favourite.v, report, editSavedFavourite, editSavedFavourite, true);
      }
      catch (err) {
        dispatch(new CreateFavouriteDefinitionFailure(instanceKey, report, err));
        navigate.toStartupView();
      }
    },

  createCompanyReportDraftDefinition: (
    favouriteId: number,
    companyReportId: number,
    editSavedFavourite: boolean
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const favouriteData = await createDraftAndHandleErrors(favouriteId, dispatch);
        navigation.toCompanyReportDraftFavourite(favouriteData.favourite.v, companyReportId, editSavedFavourite, true);
      }
      catch (err) {
        dispatch(new CreateFavouriteDefinitionFailure(instanceKey, companyReportId, err));
        navigate.toStartupView();
      }
    },

  loadReportCategory: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {

      dispatch(new actions.RequestReportCategory(instanceKey));

      const reportCategory = await api.reportCategory();
      const codes = reportCategory && reportCategory.rootCodes && reportCategory.rootCodes.map(codeItemToStoreValue);

      dispatch(new actions.ReceiveReportCategory(instanceKey, codes));
    }),

  loadSortOptions: (reportId: number, query?: string): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestSortOptions(instanceKey));
      const state = getState().favourite as FavouriteState;
      const favourite = state.instances?.[instanceKey]?.workingDefinition?.favourite?.v;
      const sortOptions = await api.sortOptions(reportId, favourite, query);
      dispatch(new actions.ReceiveSortOptions(instanceKey, mapSortOptions(sortOptions?.sortOptions || [])));
    }),

  loadFavouriteDefinition: (params: LoadFavouriteDefinitionParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        const favouriteId = params.favouriteId;

        dispatch(new actions.RequestFavouriteDefinition(instanceKey));

        const definition = await api.definition(favouriteId);

        dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));

      } catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, params, err));
        navigate.toStartupView();
      }
    },

  loadFavouriteDefinitionEmbedded: (params: LoadFavouriteDefinitionEmbeddedParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        const favouriteId = params.favouriteId;

        dispatch(new actions.RequestFavouriteDefinition(instanceKey));

        const definition = await api.definition(favouriteId);

        dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));
      } catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, params, err));
      }
    },

  loadCompanyReportFavouriteDefinition: (params: LoadFavouriteDefinitionParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        const favouriteId = params.favouriteId;
        const queryParams = navigate.queryFromSearchParams(location.search);
        const queryParamsMapped = mapQueryParams(queryParams);

        !!instanceKey && dispatch(new actions.RequestFavouriteDefinition(instanceKey));

        const definition = await api.definition(favouriteId);
        const companyReportId = definition?.reports?.filter(r => r.report?.v === ReportId.CompanyReport)[0]?.specialReport?.v;
        !!companyReportId && navigation.toSavedCompanyReport(favouriteId, companyReportId, queryParamsMapped);

        !!instanceKey && dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));

      } catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, params, err));
        navigate.toStartupView();
      }
    },

  createCompanyReportDefinition: (
    companyReportId: number,
    favouriteId: number,
    eMailReport: boolean = false,
    userEmail: string
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const existingFavourite = !!favouriteId;

      try {
        let definition = null;
        const utcOffset = getState().app.settings.utcOffset;

        if (existingFavourite) {
          dispatch(new actions.RequestFavouriteDefinition(instanceKey));
          definition = await api.definition(favouriteId);

          const emailSubscribers = eMailReport && (definition.emailSubscribers || !definition.validState && userEmail);
          if (!!emailSubscribers) {
            definition = {
              ...definition,
              ...({ emailSubscribers: emailSubscribers })
            };
            dispatch(companyReportActionCreators.actionCreators.setEmail(emailSubscribers));
          }
          dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));
        }
        else {
          dispatch(new RequestCreateSpecialReport());
          const companyReportDefinition = await api.createCompanyReportDefinition(companyReportId, { utcOffset });
          const favouriteIdNew = companyReportDefinition?.data;
          const userMessages = companyReportDefinition?.messages?.userMessages;

          navigation.toCompanyReportDraftFavourite(favouriteIdNew, companyReportId, false, true, undefined);

          !!userMessages?.length
            && userMessages.forEach(err =>
              dispatch(new ErrorNotification(err.message))
            );

          const queryParams = navigate.queryFromSearchParams(location.search);
          const queryParamsMapped = mapQueryParams(queryParams);

          definition = await api.definition(favouriteIdNew);
          definition = favDefTransforms.updateDefinitionWithReport(reportTypes.specialReport)(definition);
          definition = favDefTransforms.updateSelectedReport(
            definition, { specialReport: { v: companyReportId } }
          );
          definition = favDefTransforms.updateConditions(definition, []);
          if (eMailReport) {
            dispatch(companyReportActionCreators.actionCreators.setEmail(userEmail));
          }
          dispatch(new ReceiveCreateSpecialReport());
          navigation.toCompanyReportDraftFavourite(favouriteIdNew, companyReportId, false, true, queryParamsMapped);
        }
      }
      catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, { favouriteId }, err));
        navigate.toStartupView();
      }
    },

  validateFavouriteFilters: (translation: any): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {

      dispatch(new ErrorNotification(translation.some_fields_are_filled_out_incorrectly));
      dispatch(new actions.ValidateFavouriteFilters(instanceKey));
    }),

  displayExportToZip: (): any => {
    return new DisplayModal(MODAL.exportToZip);
  },

  createSearchDefinition: (report: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const utcOffset = getState().app.settings.utcOffset;
        const favourite = await api.createSearchDefinition({ utcOffset });
        navigation.toCaseListing(favourite, report, true, false, true);
      }
      catch (err) {
        dispatch(new CreateFavouriteDefinitionFailure(instanceKey, report, err));
        navigate.toStartupView();
      }
    },

  downloadReport: (data: FavouriteReportDownload): ActionDispatching<any, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const toastMessage = toastFactory({ autoClose: AutoCloseDuration.Longer });
      try {
        dispatch(new actions.RequestDownloadReport(instanceKey));
        const areaState = getState().favourite;
        const translationDescription = createDescriptionSelector(areaState.texts);

        toastMessage.dispatchSuccess(translationDescription(searchTranslation.download_report_wait));

        const definition = await api.definition(data.favouriteId);

        const newDefinition = {
          ...definition,
          selectedReport: { v: data.reportId }
        };

        await api.downloadReport(newDefinition);

        dispatch(new actions.ReceiveDownloadReport(instanceKey));
        toastMessage.updateSuccess(data.successMessage);

      } catch (err) {
        dispatch(new actions.FailDownloadReport(instanceKey));
        toastMessage.updateError(err.validationErrors || err.message);
      }
    },
  /**
   * Get url for opeining multiple suppliers in survey manager
   */
  getMulitpleSuppliersUrl: (): ActionDispatching<any, any> =>
  (async (dispatch: any, getState: any) => {
    try {
      dispatch(new actions.RequestUrlMultipleSuppliers(instanceKey));
      const multipleSuppliersUrl = await api.multipleSuppliersUrl();
      dispatch(new actions.ReceiveUrlMultipleSuppliers(instanceKey, multipleSuppliersUrl));
    }
    catch (err) {
      dispatch(new actions.FailReceiveUrlMultipleSuppliers(instanceKey, err));
    }
  }),

  getSearchRequiredUsers: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestSearchRequiredUsers(instanceKey));
      const searchRequiredUsers = await api.searchRequiredUsers();
      dispatch(new actions.ReceiveSearchRequiredUsers(instanceKey, searchRequiredUsers));
    }),

  transformWorkingDefinition: (transform: (definition: FavouriteDefinition) => FavouriteDefinition) =>
    errorSafe(async (dispatch: any, getState: any) => {
      const workingDefinition = selectWorkingDefinition(instanceKey)(getState());

      if (!workingDefinition || !transform) {
        return;
      }

      let newDefinition = produce(workingDefinition, transform);

      if (!favouriteDefinitionsAreEqual(workingDefinition, newDefinition)) {
        const data = await api.saveFavouriteDefinition(newDefinition);
        if (data?.selectedReport?.ctg) { // This category information is only available in the response of the saveFavouriteDefinition API
          newDefinition = {
            ...newDefinition,
            reports: data.reports,
            selectedReport: {
              ...newDefinition.selectedReport,
              ctg: data.selectedReport.ctg
            }
          };
        }
        dispatch(new actions.UpdateWorkingDefinition(instanceKey, newDefinition));
      }
    }),

  setWorkingDefinitionSelectedReport: (reportId: number) =>
    actionCreators(instanceKey).transformWorkingDefinition(def =>
      favDefTransforms.updateDefinitionWithReport(reportId)(def)
    ),

  setWorkingDefinitionSortOptions: (reportId: number, sortOrderFields: DefaultSortOrder[]) =>
    actionCreators(instanceKey).transformWorkingDefinition(def =>
      favDefTransforms.updateDefinitionWithSort(reportId, sortOrderFields)(def)
    ),

  setWorkingDefinitionQuery: (query: string) =>
    actionCreators(instanceKey).transformWorkingDefinition(def =>
      favDefTransforms.updateDefinitionWithQuery(query)(def)
    ),

  setWorkingDefinitionAggregation:
    (
      aggregationDomain: Value,
      aggregationType: Value,
      uomSetDomain: Value,
      clearSections?: { target?: boolean; rollingAverage?: boolean; selections?: boolean }
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => {
          if (clearSections?.target) {
            def = favDefTransforms.transformSelectedReport({ targetApp: undefined })(def);
          }
          if (clearSections?.rollingAverage) {
            def = favDefTransforms.transformSelectedReport({ rollingAverage: undefined })(def);
          }
          if (clearSections?.selections) {
            def = favDefTransforms.transformSelectedReport({ selections: [] })(def);
          }
          return favDefTransforms.updateAggregation(def, aggregationDomain, aggregationType, uomSetDomain);
        }
      ),
  setWorkingDefinitionTargetApplication:
    (
      targetApplication: Value
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateTarget(def, targetApplication)
      ),
  setWorkingDefinitionExposure:
    (
      exposureApplication: Value,
      exposureFactor?: number,
      exposurePercent?: number
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => {
          if (def.exposureApplication?.v !== exposureApplication?.v) {
            // If exposure application has changed, rolling average and selections should be cleared
            def = favDefTransforms.transformSelectedReport({ rollingAverage: undefined, selections: [] })(def);
          }
          return favDefTransforms.updateExposure(def, exposureApplication, exposureFactor ?? 0, exposurePercent ?? 0);
        }
      ),
  setWorkingDefinitionSelections:
    (
      selectedGroupByFields: SelectionDefinition[],
      rollingAverage?: number
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateGroupBy(def, selectedGroupByFields, rollingAverage)
      ),
  setWorkingDefinitionLayoutAttributes:
    (
      attributes: { [key: string]: string | boolean | number }[],
      layoutType: number,
      layoutTypeContext: number
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateFavouriteLayoutAttributes(def, attributes, layoutType, layoutTypeContext)
      ),

  setWorkingDefinitionLayoutType:
    (
      layoutType: Value
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateLayoutType(def, layoutType)
      ),

  setWorkingDefinitionDrillDownReport:
    (
      drillDownReport: Value
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateDrillDownReport(def, drillDownReport)
      ),

  setWorkingDefinitionDrillDownLayoutType:
    (
      drillDownReport: Value
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateDrillDownLayoutType(def, drillDownReport)
      ),

  loadDashboardFavouriteDefinition: (params: LoadFavouriteDefinitionParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        const favouriteId = params.favouriteId;

        dispatch(new actions.RequestFavouriteDefinition(instanceKey));

        const definition = await api.definition(favouriteId);
        const dashboardType = definition?.reports?.filter(r => r.report?.v === ReportId.Dashboard)[0]?.dashboard?.v;
        const queryParams = navigate.queryFromSearchParams(location.search);
        const queryParamsMapped = mapQueryParams(queryParams);
        !!dashboardType && navigation.toSavedDashboard(favouriteId, dashboardType, queryParamsMapped);

        dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));

      } catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, params, err));
        navigate.toStartupView();
      }
    },

  createDashboardDraftDefinition: (
    favouriteId: number,
    dashboardType: number,
    editSavedFavourite: boolean
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const favouriteData = await createDraftAndHandleErrors(favouriteId, dispatch);
        navigation.toDashboardDraftFavourite(favouriteData.favourite.v, dashboardType, editSavedFavourite, true);
      }
      catch (err) {
        dispatch(new CreateFavouriteDefinitionFailure(instanceKey, dashboardType, err));
        navigate.toStartupView();
      }
    },

  setWorkingDefinitionIncludedFields:
    (
      includedFields: CaseIncludedField[]
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateIncludedDomains(def, includedFields)
      ),

  updateReplaceDomainNodes: (transform: (definition: FavouriteDefinition) => FavouriteDefinition,
    onSuccess: () => void) =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const toastMessage = toastFactory({ autoClose: AutoCloseDuration.Longer });
      try {
        dispatch(new actions.RequestUpdateReplaceDomainNodes(instanceKey));
        const areaState = getState().favourite;
        const translationDescription = createDescriptionSelector(areaState.texts);
        const workingDefinition = selectWorkingDefinition(instanceKey)(getState());

        if (!workingDefinition || !transform) {
          return;
        }

        const newDefinition = produce(workingDefinition, transform);

        if (!favouriteDefinitionsAreEqual(workingDefinition, newDefinition)) {
          await api.saveReplaceDomainNodes(newDefinition);
          dispatch(new actions.ReceiveUpdateReplaceDomainNodes(instanceKey, newDefinition));
          onSuccess();
          toastMessage.dispatchSuccess(translationDescription(searchTranslation.filter_replacements_updated_successfully));
        }
      } catch (err) {
        dispatch(new actions.FailUpdateReplaceDomainNodes(instanceKey));

        !!err?.userMessages?.length
          && err?.userMessages.forEach(error =>
            dispatch(new ErrorNotification(error.message))
          );
      }
    },

  loadExtendedReplaceDomainNodes: (
    favouriteId: number,
    onSuccess: (replaceDomainNodes: FavouriteReplaceDomainNodes[]) => void
  ): ActionDispatching<any, any> => (async (dispatch: any, getState: any) => {
    try {
      dispatch(new actions.RequestExtendedReplaceDomainNodes(instanceKey));
      const currentDefinition = await api.loadExtendedReplaceDomainNodes(favouriteId);
      const extendedReplaceDomainNodes = currentDefinition?.replaceDomainNodes || [];
      dispatch(new actions.ReceiveExtendedReplaceDomainNodes(instanceKey));
      onSuccess(extendedReplaceDomainNodes);
    }
    catch (err) {
      dispatch(new actions.FailExtendedReplaceDomainNodes(instanceKey, err));
      !!err?.userMessages?.length
        && err?.userMessages.forEach(err =>
          dispatch(new ErrorNotification(err.message))
        );
    }
  })
});
